import {useEffect, useRef, useState} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { GetPublicBuildingById } from '../../../reduxToolkit/Building/BuildingApi';
import {BUILDING_DETAIL_MODEL} from "../Model/Model";
import {calculateHeight, errorMsg, isSmWidth, tryGetAddress} from '../../../Apis/functions';
/**
 * Hook component for managing building details.
 * Handles fetching and updating of the building data.
 *
 * @Author: karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer
 * @Email : kpozos@ibl.mx
 */
const BuildingDetailsApp = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    // The list opens the detail with the building in the state of the route
    const { state } = useLocation();
    const buildingId = state?.id ?? id;
    const dispatch = useDispatch();
    const DEV = useSelector((state) => state?.CommonsSlice?.development); // Fetch development flag from store
    const { permission } = useSelector((state) => state.authenticatioauthennSlice); // Fetch user permissions from store
    const navigate = useNavigate();
    const [modified, setModified] = useState(false);
    // Building shown by the information card, it comes from the detail endpoint
    const [buildingDetail, setBuildingDetail] = useState(BUILDING_DETAIL_MODEL);
    const [showManagerUserListModal, setShowManagerUserListModal] = useState(false);
    const [userClassificationSelected, setUserClassificationSelected] = useState(null);
    const containerRef = useRef(null);
    const [containerHeight, setContainerHeight] = useState('auto');

    useEffect(() => {
        if (isSmWidth()) return;

        if (containerRef.current) {
            calculateHeight({
                elementRef: containerRef,
                bottom: 50,
                setHeight:setContainerHeight
            });
        }
    }, []);

    // Loads the building of the detail
    useEffect(() => {
        getBuildingDetail();
    }, []);

    /**
     * Resolves the address of the building out of its coordinates, the detail
     * endpoint only returns the position.
     *
     * @effect
     */
    useEffect(() => {
        const {latitude, longitude} = buildingDetail;
        if (!latitude || !longitude) return;

        setBuildingDetail((prev) => ({...prev, location: t('loading')}));
        tryGetAddress(latitude, longitude, dispatch, (address) => {
            setBuildingDetail((prev) => ({...prev, location: address}));
        });
    }, [buildingDetail.latitude, buildingDetail.longitude]);

    /**
     * Loads the building rendered by the information card.
     *
     * @function getBuildingDetail
     */
    const getBuildingDetail = () => {
        if (!buildingId) return;

        dispatch(GetPublicBuildingById(buildingId)).then((response) => {
            const {success, data, code} = response?.payload?.data || {};
            if (!success) {
                errorMsg(code);
                return;
            }
            setBuildingDetail(BUILDING_DETAIL_MODEL.set(data));
        });
    };

    // Reloads the building when a modification occurs, the identity verification
    // section is counted by its detail
    useEffect(() => {
        if(modified){
            getBuildingDetail();
            setModified(false)
        }
    }, [modified]);

    return {
        t,
        navigate,
        id: buildingId,
        buildingDetail,
        DEV,
        permission,
        modified, setModified,
        showManagerUserListModal,
        setShowManagerUserListModal,
        userClassificationSelected,
        setUserClassificationSelected,
        containerHeight,
        containerRef
    };
};

export default BuildingDetailsApp;
